import React, { useEffect, useState } from "react";
import {
    Box, Typography, Button, Dialog, DialogActions,
    DialogContent, DialogTitle, Paper, TextField
} from "@mui/material";
import GroupsIcon from "@mui/icons-material/Groups";
import AddIcon from "@mui/icons-material/Add";

import {
    getGardenGroups,
    createGardenGroup,
} from "../api/groups";
import { useTranslate } from "../components/Translate/TranslateContext";
import GroupModal from "../components/Groups/GroupModal";

export default function Groups({ user, farms }) {
    const { translate } = useTranslate();

    const [groups, setGroups] = useState([]);
    const [createOpen, setCreateOpen] = useState(false);
    const [newGroupName, setNewGroupName] = useState("");
    const [loading, setLoading] = useState(false);

    const [selectedGroup, setSelectedGroup] = useState(null);
    const [groupModalOpen, setGroupModalOpen] = useState(false);

    const loadGroups = async () => {
        try {
            setLoading(true);
            const res = await getGardenGroups();
            setGroups(res.groups || []);
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadGroups();
    }, []);

    const handleCreate = async () => {
        if (!newGroupName.trim()) return;

        try {
            setLoading(true);
            await createGardenGroup(user, newGroupName.trim(), []);
            setCreateOpen(false);
            setNewGroupName("");
            await loadGroups();
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <Paper
                elevation={0}
                sx={{
                    borderRadius: 4,
                    p: { xs: 2, sm: 3 },
                    border: "1px solid rgba(0,0,0,0.08)",
                    backgroundColor: "var(--color-bg-primary)",
                    minHeight: 400,
                    display: "flex",
                    flexDirection: "column",
                }}
            >
                <Box sx={{
                    display: "flex",
                    flexDirection: { xs: "column", sm: "row" },
                    justifyContent: "space-between",
                    alignItems: { xs: "flex-start", sm: "center" },
                    gap: 2,
                    mb: 3,
                }}>
                    <Box sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                    }}>
                        <GroupsIcon sx={{
                            color: "var(--color-green-main)",
                        }} />

                        <Typography sx={{
                            fontFamily: "Fraunces",
                            fontSize: { xs: 22, sm: 26 },
                            color: "var(--color-text-primary)",
                        }}>
                            {translate("garden_groups")}
                        </Typography>
                    </Box>

                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => setCreateOpen(true)}
                        sx={{
                            backgroundColor: "var(--color-green-main)",
                            color: "var(--color-text-inverse)",
                            borderRadius: 3,
                            textTransform: "none",
                            width: { xs: "100%", sm: "auto" },
                            fontSize: { xs: "0.85rem", sm: "0.875rem" },
                            py: { xs: 1, sm: 0.75 },
                            "&:hover": {
                                backgroundColor: "var(--color-green-strong)",
                            },
                        }}
                    >
                        {translate("create")}
                    </Button>
                </Box>

                <Box sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                    overflowY: "auto",
                }}>
                    {!groups.length && !loading && (
                        <Typography sx={{
                            textAlign: "center",
                            color: "text.secondary",
                            py: 6,
                        }}>
                            {translate("no_groups_created")}
                        </Typography>
                    )}

                    {groups.map((group) => (
                        <Paper
                            key={group.group_id}
                            elevation={0}
                            onClick={() => {
                                setSelectedGroup(group);
                                setGroupModalOpen(true);
                            }}
                            sx={{
                                p: 2,
                                borderRadius: 3,
                                backgroundColor: "var(--color-green-soft)",
                                border: "1px solid var(--color-green-strong)",
                                cursor: "pointer",
                                transition: "0.2s ease",
                                "&:hover": {
                                    transform: "translateY(-2px)",
                                    boxShadow: "0 8px 20px rgba(0,0,0,0.08)",
                                },
                            }}
                        >
                            <Typography sx={{
                                fontWeight: 700,
                                color: "var(--color-text-primary)",
                            }}>
                                {group.group_name}
                            </Typography>

                            <Typography
                                variant="body2"
                                sx={{ color: "text.secondary" }}
                            >
                                {group.gardens?.length || 0} {translate("gardens")}
                            </Typography>
                        </Paper>
                    ))}
                </Box>
            </Paper>

            <Dialog
                open={createOpen}
                onClose={() => setCreateOpen(false)}
                fullWidth
                maxWidth="sm"
            >
                <DialogTitle>
                    {translate("create_group")}
                </DialogTitle>

                <DialogContent>
                    <TextField
                        fullWidth
                        autoFocus
                        margin="dense"
                        label={translate("new_group_name")}
                        value={newGroupName}
                        onChange={(e) => setNewGroupName(e.target.value)}
                    />
                </DialogContent>

                <DialogActions>
                    <Button onClick={() => setCreateOpen(false)}>
                        {translate("cancel")}
                    </Button>

                    <Button
                        variant="contained"
                        disabled={loading}
                        onClick={handleCreate}
                    >
                        {translate("create")}
                    </Button>
                </DialogActions>
            </Dialog>

            <GroupModal
                open={groupModalOpen}
                onClose={() => {
                    setGroupModalOpen(false);
                    setSelectedGroup(null);
                }}
                group={selectedGroup}
                farms={farms}
                user={user}
                onRefresh={loadGroups}
            />
        </>
    );
}